import axios from "axios";
import { baseURL } from "../Constants";
import { Donator } from "../models/Donator";

const url = `${baseURL}/Donator`;

const CACHE_KEY = 'donators_cache';
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

interface CachedDonators {
    data: Donator[];
    timestamp: number;
}

export const getAllDonators = async (): Promise<Donator[]> => {
    // Check localStorage cache first
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const { data, timestamp }: CachedDonators = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION_MS) {
                return data; // Return cached data if fresh
            }
        }
    } catch (e) {
        // If cache parsing fails, just fetch fresh
        console.warn('Failed to parse donators cache:', e);
    }

    // Fetch from API and update cache
    try {
        const response = await axios.get<Donator[]>(url);
        const donators = response.data;

        localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: donators,
            timestamp: Date.now()
        }));

        return donators;
    } catch (error) {
        console.error('Error fetching donators:', error);

        // If fetch fails but we have stale cache, return it
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { data }: CachedDonators = JSON.parse(cached);
                return data;
            }
        } catch (e) {
            // Ignore
        }

        return [];
    }
};

export const getDonationAmount = (steamId: string, donators: Donator[]): number => {
    const playerDonations = donators.filter(d => d.steamId === steamId);
    return playerDonations.reduce((sum, d) => sum + d.donationAmount, 0);
};

export const clearDonatorsCache = (): void => {
    localStorage.removeItem(CACHE_KEY);
};

export const getDonatorBySteamId = async (steamId: string): Promise<{
    steamId: string;
    donations: Donator[];
    totalDonated: number;
    isDonator: boolean;
    isPremium: boolean;
} | null> => {
    try {
        const response = await axios.get(`${url}/${steamId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching donator:', error);
        return null;
    }
};

// ---- Admin management (requires an admin JWT in localStorage) ----

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export interface DonationPayload {
    steamId: string | null;
    name: string;
    amount: number;
    createdAt?: string | null;
}

// Fresh (non-cached) list for the admin page.
export const getDonatorsForAdmin = async (): Promise<Donator[]> => {
    const response = await axios.get<Donator[]>(url);
    return response.data;
};

export const addDonation = async (
    payload: DonationPayload & { setDonatorFlag: boolean }
): Promise<{ donation: Donator; donatorFlagSet: boolean }> => {
    const response = await axios.post(`${url}/admin`, payload, { headers: authHeader() });
    clearDonatorsCache();
    return response.data;
};

export const updateDonation = async (id: number, payload: DonationPayload): Promise<void> => {
    await axios.put(`${url}/admin/${id}`, payload, { headers: authHeader() });
    clearDonatorsCache();
};

export const deleteDonation = async (id: number): Promise<void> => {
    await axios.delete(`${url}/admin/${id}`, { headers: authHeader() });
    clearDonatorsCache();
};
